<template>
	<div>
		<!-- Compact centered "Create new folder" pop-up (matches design:
		     352px wide, 8px radius, 1px surface-3 border; content padded
		     20px vertical / 24px horizontal with 16px gaps). -->
		<ModalBox v-model="legacyModalVisible" size="sm" hide-close header-class="!hidden"
			wrapper-classes="lg:!w-[352px] !rounded-lg ring-1 ring-surface-3">
			<div class="flex flex-col gap-4 px-6 py-5">
				<h2 class="text-base font-medium text-white">Create new folder</h2>

				<!-- Generic / subscription error -->
				<Alert v-if="error && !error.role" type="error" hideIcon>
					<div v-html="error.message"></div>
					<div v-if="error.subscription" class="text-center mt-2">
						<a href="/app/subscribe">
							<FormButton type="success" size="md">upgrade now</FormButton>
						</a>
					</div>
				</Alert>

				<!-- No subscription → offer trial -->
				<div v-if="error && error.role">
					<p class="text-sm text-surface-9">
						You do not have an active VOD subscription.<br>
						Would you like to activate our 7-day trial?
					</p>
					<div v-if="trialError" class="color-danger text-sm mt-2">{{ trialError.message }}</div>
				</div>

				<!-- Folder name form -->
				<div v-else>
					<label class="text-sm text-surface-9 block mb-2">Folder name</label>
					<FormInput v-model="channel.name" placeholder="e.g Product demos, Tutorials, Events"
						@keypress="onInputChange('name')" />
					<p v-show="formErrors.name" class="color-danger text-sm mt-1">Folder name is required</p>
				</div>

				<!-- Actions -->
				<div class="flex justify-end gap-2">
					<FormButton type="secondary" @click="dismiss">Cancel</FormButton>
					<FormButton v-if="error && error.role" type="main" :disabled="trialProcessing"
						@click="forceActivateTrial">
						{{ trialProcessing ? 'Activating…' : 'Activate Trial' }}
					</FormButton>
					<FormButton v-else type="main"
						:disabled="processing || !channel.name || !channel.name.trim()" @click="onSaveChannel">
						{{ processing ? 'Creating…' : 'Create folder' }}
					</FormButton>
				</div>
			</div>
		</ModalBox>
	</div>
</template>

<script>
import FormProgress from '@/components/Atoms/FormProgress.vue'
import ModalBox from '@/components/modals/ModalBox.vue'
import { legacyModalBridgeMixin } from '@/composables/useLegacyModalBridge'
import _ from 'lodash';
import Spinner from "@/components/ui/Spinner.vue";
import StreamService from '@/services/StreamService';
import IconBase from '@/components/icon/IconBase.vue';
import IconLivestream from '@/components/icon/IconLivestream.vue';
import IconMultistream from '@/components/icon/IconMultistream.vue';
import IconIpcam from '@/components/icon/IconIpcam.vue';
import IconSchedulestream from '@/components/icon/IconSchedulestream.vue';
import IconVodChannel from '@/components/icon/IconVodChannel.vue';
import SubscriptionService from '@/services/SubscriptionService';
import FormButton from '@/components/Atoms/FormButton.vue';
import Alert from '@/components/Atoms/Alert.vue';
import FormInput from '@/components/Atoms/FormInput.vue';
import { track, EVENTS } from '@/lib/tracking';
export default {
	name: 'AddVodModal',
	mixins: [legacyModalBridgeMixin],
	legacyModalId: 'modal-add-vod',
	components: {
		FormProgress,
		ModalBox,
		Spinner,
		IconBase,
		IconLivestream,
		IconMultistream,
		IconIpcam,
		IconSchedulestream,
		IconVodChannel,
		FormButton,
		Alert,
		FormInput,
	},
	data() {
		return {
			processing: false,
			trialProcessing: false,
			error: null,
			trialError: null,
			operational: true,
			viewStage: 1,
			selectedStreamType: 'vod',
			userSubscription: null,
			channel: {
				name: null
			},
			selectedRegion: null,
			regions: [],
			formErrors: { name: false, region: false },
			streamTypeList: [
				{
					type: 'vod',
					title: 'New VOD Bucket',
					description: 'HLS Videos With Castr CDN',
					description2: 'Embed videos on your website and OTT platforms.',
					icon: require('@/assets/images/vod-plan.png'), // eslint-disable-line no-undef
					iconAlt: 'fa fa-stream'
				}
			],
			onInputChange(prop) {
				this.formErrors[prop] = false;
			}
		};
	},
	computed: {
		subscriptions() {
			return this.$store.state.User.subscriptions;
		},
		contextualRegionList() {
			const stype = 'vod';
			return stype
				? this.regions.filter(r => r.platforms.indexOf(stype) > -1)
				: [];
		},
		streamTypeSubscriptionCategory() {
			return 'vod';
		}
	},
	async mounted() {
		this.selectedStreamType = 'vod';
		// Regions are loaded when the modal opens so GET /regions/vod does not
		// sit on the Content Library page-load path.
	},
	methods: {
		onLegacyModalShow() {
			this.onInit();
		},
		onLegacyModalHide() {
			this.onDismiss();
		},
		async onInit() {
			this.operational = true;
			this.error = null;
			this.trialError = null;
			this.trialProcessing = false;
			this.selectedStreamType = 'vod';
			await this.onStreamTypeSelected();
		},
		onDismiss() {
			this.resetForm();
			this.clearErrors();
			// lazy clear
			setTimeout(() => {
				this.processing = false;
			}, 1000);
		},
		dismiss() {
			this.hideLegacyModal();
			this.onDismiss();
		},
		getCountryFlag(region) {
			return `https://assets.castr.io/countryflags/${region.identifier}/flat/64.png`;
		},
		async onStreamTypeSelected() {
			this.operational = true;
			this.error = null;
			this.trialError = null;
			this.trialProcessing = false;

			const subCat = this.selectedStreamType;
			const hasRequiredSub = this.subscriptions.find(
				(s) => {
					const chain = (subCat === 'restream') ? 'package.category': 'category';
					return _.get(s, chain, '').includes(subCat);
				}
			);

			if (!hasRequiredSub) {
				this.operational = false;
				this.error = { role: true };
			}

			// If client has assigned deployment region for certain stream type
			// we need to show only those regions when creating new stream
			// const platformNode = _.find(this.deploymentRegions, pnode =>
			// 	_.includes(pnode.platforms, subCat)
			// );
			// if (platformNode && !_.isEmpty(platformNode.regions)) {
			// }

			// Use Vuex store for regions (already loaded at app level)
			await this.$store.dispatch('Regions/getRegions', subCat);
			this.regions = _.cloneDeep(this.$store.getters['Regions/getRegionsByCategory'](subCat));

			if (this.contextualRegionList.length) {
				this.channel.region = this.contextualRegionList[0]._id;
			}
		},
		unselectStreamType() {
			// this.selectedStreamType = null;
			this.resetForm();
			this.clearErrors();
			this.dismiss();
		},
		async forceActivateTrial() {
			this.trialError = null;
			this.trialProcessing = true;

			// activate trial
			const subCat = this.selectedStreamType;
			try {
				await SubscriptionService.requestTrial(subCat);
				// update vuex state
				await this.$store.dispatch('User/loadUser', true, { root: true });
			} catch (e) {
				this.trialError = e;
				return;
			} finally {
				this.trialProcessing = false;
			}

			this.error = null;
			this.operational = true;

			// on trial activated
			if (!this.trialError) {
				track(EVENTS.TRIAL_STARTED, { packCategory: 'vod' });
				this.$notify({
					group: 'success',
					text: `${this.selectedStreamType} trial activated successfully`
				});
			}
		},
		async onSaveChannel() {
			this.error = null;
			const stype = 'vod';
			if (!this.validateForm()) return;
			this.processing = true;

			try {
				// const stream = await StreamService.addStream(
				const newStream = await StreamService.addStream(
					this.channel.name,
					this.channel.region,
					'vod'
				);
				// #custom-patch: new stream not found in api response
				const streamList = await StreamService.getUserStreams();
				const stream = _.find(
					streamList,
					s =>
						s.type === 'vod' &&
                  s.name === this.channel.name
				);
				this.$emit('new-channel', stream, this.selectedRegion);
				this.dismiss();
				await this.$store.dispatch('Streams/getStreams', true);
				track(EVENTS.VOD_FOLDER_CREATED, { folderName: this.channel.name });
				// BUG-42: stay on the dashboard after creating a folder (no redirect into it)
				// BUG-41: show success toast
				this.$notify({
					group: 'success',
					text: 'Folder created successfully',
				});
			} catch (err) {
				this.error = err;
				this.processing = false;
				if (err.message === 'The VOD quota is exceeded') {
					this.error.message = `<i class="fas fa-exclamation-triangle mr-2"></i>You've reached the soft limit for your video hosting folder quota. This limit can be increased for free. To increase your limit, please contact our <span class="text-underline cursor-pointer" onclick="Intercom('show')">customer support team</span>.`
				} else err.message = '<i class="fas fa-exclamation-triangle mr-2"></i>'+err.message;

				if(this.selectedStreamType === 'vod') {
					err.message = err.message.replace('streams and platforms', 'VOD Bucket');
				}
				// check if subscripiton error
				if (err && _.indexOf(err.message, 'subscription') > -1) {
					this.error.subscription = true;
				}
			}
		},
		validateForm() {
			const props = ['name'];
			let valids = 0;
			_.each(props, prop => {
				const val = this.channel[prop];
				if (val) valids++;
				// else this.formErrors[prop] = true;

				this.formErrors[prop] = !val;
			});

			return valids === props.length;
		},
		clearErrors() {
			this.error = null;
			this.formErrors.name = false;
		},
		resetForm() {
			this.channel.name = null;
		}
	}
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.stream-item__p{
	cursor: pointer;
}
@media (max-width: 767px) {
	:deep(* .modal-dialog-centered) {
		align-items: flex-end;
	}	
	.mt-100 {
		margin-top: 0;
	}
}
</style>
